import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { ArrowRight, BookOpen, Search } from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";

const TOPIC_HINTS = {
  insulin_resistance: { tone: "emerald", lead: "Often the hidden driver behind belly fat, energy crashes and PCOS." },
  low_energy: { tone: "amber", lead: "Why you crash at 3pm — and how to bring back steady, all-day focus." },
  pcos: { tone: "emerald", lead: "A calm, evidence-led approach to PCOS — beyond the cycle conversation." },
  fatty_liver: { tone: "emerald", lead: "Surprisingly common, and one of the most reversible conditions on this list." },
  metabolism: { tone: "amber", lead: "What 'slow metabolism' really means — and the small habits that quietly fix it." },
  menopause: { tone: "emerald", lead: "Hormone-changes wellness support, alongside whatever clinical care you're already on." },
  belly_fat: { tone: "amber", lead: "Why crunches won't shift it — and what consistently does." },
};

export default function LearnIndexPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useSeo({
    title: "Wellness Library — Plain-language guides | Feel Greats",
    description:
      "Short, evidence-led guides to PCOS, insulin resistance, fatty liver, menopause, metabolism, low energy and belly fat — reviewed by our wellness team.",
    canonical: `${PROD_DOMAIN}/learn`,
    ogType: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Feel Greats Wellness Library",
      "description":
        "Plain-language wellness guides on insulin resistance, PCOS, fatty liver, menopause, belly fat, metabolism and energy.",
      "url": `${PROD_DOMAIN}/learn`,
    },
  });

  useEffect(() => {
    document.title = "Wellness Library — Plain-language guides | Feel Greats";
    (async () => {
      try {
        const { data } = await api.get("/learn");
        setItems(data || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = items.filter((a) =>
    !q || (a.title + " " + a.hero_excerpt + " " + (a.condition_key || "")).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-12" data-testid="learn-index-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <header className="text-center max-w-3xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold flex items-center justify-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Wellness library
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-emerald-950 font-light tracking-tight mt-3">
            Plain-language guides for the conditions members ask about most.
          </h1>
          <p className="mt-4 text-emerald-900/70 leading-relaxed">
            Short reads, no jargon, no fear-mongering. Every guide is reviewed by our wellness team
            and refreshed when the evidence changes.
          </p>
        </header>

        <div className="max-w-md mx-auto relative">
          <Search className="w-4 h-4 text-emerald-700 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search topics — e.g. PCOS, fatty liver, sleep…"
            className="w-full rounded-full border border-emerald-200 bg-white py-3 pl-11 pr-4 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30"
            data-testid="learn-search-input"
          />
        </div>

        {loading ? (
          <p className="text-center text-emerald-700">Loading library…</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-emerald-900/60">Nothing matches that — try another keyword.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((a) => {
              const hint = TOPIC_HINTS[a.condition_key] || { tone: "emerald", lead: a.hero_excerpt };
              const lead = hint.lead || a.hero_excerpt;
              return (
                <Link
                  key={a.id}
                  to={`/learn/${a.slug}`}
                  className="group"
                  data-testid={`learn-card-${a.slug}`}
                >
                  <Card className="rounded-2xl border-emerald-100 bg-white h-full hover:shadow-md hover:border-emerald-200 transition-all duration-200">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${hint.tone === "amber" ? "text-amber-700" : "text-emerald-700"}`}>
                        {a.condition_key?.replace(/_/g, " ") || "wellness"}
                      </div>
                      <h2 className="font-display text-lg text-emerald-950 mt-2 leading-snug group-hover:text-emerald-700 transition-colors">
                        {a.title}
                      </h2>
                      <p className="text-sm text-emerald-900/70 mt-2 leading-relaxed flex-1">
                        {lead}
                      </p>
                      <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-emerald-700 group-hover:gap-2 transition-all">
                        Read the guide <ArrowRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        <Card className="rounded-2xl border-emerald-100 bg-white">
          <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-xl text-emerald-950">Not sure where to start?</h3>
              <p className="text-sm text-emerald-900/70 mt-1">
                Take our 3-minute health assessment. We'll match the right guide and the right
                products to your specific situation.
              </p>
            </div>
            <Link to="/register" data-testid="learn-cta-assessment">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 text-sm font-semibold transition-colors">
                Take the assessment <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
